(() => {
  const SELECTOR = '[data-hiskin-mhtp-story]';
  const asBoolean = (value) => String(value).toLowerCase() === 'true';
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const setupStory = (story) => {
    if (!story || story.dataset.hiskinReady === 'true') return;
    story.dataset.hiskinReady = 'true';

    const revealItems = story.querySelectorAll('[data-hm-reveal]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('hm-is-visible'));
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('hm-is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
      revealItems.forEach((item) => observer.observe(item));
    }

    const track = story.querySelector('[data-hm-result-track]');
    const previous = story.querySelector('[data-hm-result-prev]');
    const next = story.querySelector('[data-hm-result-next]');

    if (track && previous && next) {
      const move = (direction) => {
        const card = track.querySelector('[data-hm-result-card]');
        if (!card) return;
        const gap = parseFloat(window.getComputedStyle(track).columnGap || 0);
        track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: reducedMotion ? 'auto' : 'smooth' });
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
    }

    const toneSpectrum = story.querySelector('[data-hm-tone-wheel]');
    if (toneSpectrum) {
      const toneLabel = toneSpectrum.querySelector('[data-hm-tone-label]');
      const toneWheel = toneSpectrum.querySelector('.hm-tone-wheel');
      const intensityLabel = toneSpectrum.querySelector('[data-hm-intensity]');
      const profileIntensity = toneSpectrum.querySelector('[data-hm-profile-intensity]');
      const planName = toneSpectrum.querySelector('[data-hm-plan-name]');
      const planPrice = toneSpectrum.querySelector('[data-hm-plan-price]');
      const planRationale = toneSpectrum.querySelector('[data-hm-plan-rationale]');
      const planPromo = toneSpectrum.querySelector('[data-hm-plan-promo]');
      const planSupply = toneSpectrum.querySelector('[data-hm-plan-supply]');
      const planPosition = toneSpectrum.querySelector('[data-hm-plan-position]');
      const planCta = toneSpectrum.querySelector('[data-hm-select-plan]');
      const ctaLabel = toneSpectrum.querySelector('[data-hm-cta-label]');
      const ctaPrice = toneSpectrum.querySelector('[data-hm-cta-price]');
      const stockStatus = toneSpectrum.querySelector('[data-hm-stock-status]');
      const deepSteps = toneSpectrum.querySelector('[data-hm-deep-steps]');
      const upsellCopy = toneSpectrum.querySelector('[data-hm-upsell-copy]');
      const priceSources = {};
      let lastSyncedPlanKey = '';
      toneSpectrum.querySelectorAll('[data-hm-price-source]').forEach((source) => {
        priceSources[Number(source.dataset.hmPriceSource)] = source;
      });

      const priceFallbacks = {
        2: toneSpectrum.dataset.hmPrice2,
        4: toneSpectrum.dataset.hmPrice4,
        6: toneSpectrum.dataset.hmPrice6,
        10: toneSpectrum.dataset.hmPrice10
      };
      const readPrice = (boxes) => priceSources[boxes]?.textContent.trim() || priceFallbacks[boxes] || '';

      const productPlans = {};
      [2, 4, 6, 10].forEach((boxes) => {
        productPlans[boxes] = {
          boxes,
          variantId: toneSpectrum.getAttribute(`data-hm-id-${boxes}`) || '',
          option: toneSpectrum.getAttribute(`data-hm-option-${boxes}`) || '',
          available: asBoolean(toneSpectrum.getAttribute(`data-hm-available-${boxes}`)),
          tracked: asBoolean(toneSpectrum.getAttribute(`data-hm-tracked-${boxes}`)),
          stock: Number(toneSpectrum.getAttribute(`data-hm-stock-${boxes}`) || 0),
          name: toneSpectrum.getAttribute(`data-hm-plan-${boxes}`) || ''
        };
      });

      const toneNames = [1, 2, 3, 4, 5].map((index) => toneSpectrum.getAttribute(`data-hm-tone-${index}`));
      const fillTemplate = (template, values) => Object.entries(values).reduce((copy, [key, value]) => copy.replaceAll(`[${key}]`, String(value)), template || '');

      const getToneName = (value) => {
        if (value < 25) return toneNames[0];
        if (value < 50) return toneNames[1];
        if (value < 75) return toneNames[2];
        if (value < 92) return toneNames[3];
        return toneNames[4];
      };

      const makePlan = (sourceBoxes, sets, displayBoxes, promoAttribute) => {
        const source = productPlans[sourceBoxes];
        return {
          ...source,
          sourceBoxes,
          sets,
          displayBoxes,
          name: sets === 2 ? `2 × ${source.name}` : source.name,
          structure: `${displayBoxes} boxes · ${sets} ${sets === 1 ? 'set' : 'sets'}`,
          promo: toneSpectrum.getAttribute(promoAttribute) || ''
        };
      };

      const getPlan = (value) => {
        if (value < 25) return makePlan(2, 1, 2, 'data-hm-promo-2');
        if (value < 50) return makePlan(4, 1, 4, 'data-hm-promo-4');
        if (value < 75) return makePlan(6, 1, 6, 'data-hm-promo-6');
        if (value < 92) return makePlan(10, 1, 10, 'data-hm-promo-10');
        return makePlan(10, 2, 20, 'data-hm-promo-deep');
      };

      const representativeIntensity = { 2: 12, 4: 37, 6: 62, 10: 83 };

      const planIsAvailable = (plan) => {
        if (!plan.available || !plan.variantId) return false;
        if (plan.sets === 2 && plan.tracked && plan.stock < 2) return false;
        return true;
      };

      const setIntensity = (rawValue, options = {}) => {
        const value = clamp(Math.round(rawValue), 0, 100);
        const patches = value < 25
          ? 14
          : value < 50
            ? Math.round((21 + ((value - 25) / 24) * 7) / 7) * 7
            : value < 75
              ? Math.round((35 + ((value - 50) / 24) * 7) / 7) * 7
              : value < 92
                ? Math.round((49 + ((value - 75) / 16) * 21) / 7) * 7
                : 140;
        const tone = getToneName(value);
        const plan = getPlan(value);
        const price = readPrice(plan.sourceBoxes);
        const isDeep = plan.sets === 2;
        const isAvailable = planIsAvailable(plan);

        toneSpectrum.dataset.intensity = String(value);
        toneSpectrum.dataset.hmCurrentBoxes = String(plan.sourceBoxes);
        toneSpectrum.style.setProperty('--hm-marker-angle', `${-150 + value * 3}deg`);
        toneSpectrum.classList.toggle('hm-plan-unavailable', !isAvailable);
        if (toneLabel) toneLabel.textContent = tone;
        if (intensityLabel) intensityLabel.textContent = String(value);
        if (profileIntensity) profileIntensity.textContent = String(value);
        if (planName) planName.textContent = plan.name;
        if (planPrice) planPrice.textContent = isDeep ? `${price} first set` : price;
        if (planRationale) planRationale.textContent = fillTemplate(toneSpectrum.getAttribute(isDeep ? 'data-hm-rationale-deep' : 'data-hm-rationale'), { intensity: value, patches, boxes: plan.displayBoxes });
        if (planPromo) planPromo.textContent = plan.promo;
        if (planSupply) planSupply.textContent = `${patches} patches`;
        if (planPosition) planPosition.textContent = plan.structure;
        if (deepSteps) deepSteps.hidden = !isDeep;
        if (stockStatus) {
          stockStatus.hidden = isAvailable;
          stockStatus.textContent = isDeep && plan.available
            ? toneSpectrum.getAttribute('data-hm-deep-inventory-note')
            : toneSpectrum.getAttribute('data-hm-unavailable-note');
        }
        if (planCta) {
          planCta.dataset.hmVariantId = plan.variantId;
          planCta.dataset.hmVariantOption = plan.option;
          planCta.dataset.hmSets = String(plan.sets);
          planCta.disabled = !isAvailable;
          planCta.setAttribute('aria-disabled', String(!isAvailable));
        }
        if (ctaLabel) ctaLabel.textContent = isAvailable
          ? toneSpectrum.getAttribute(isDeep ? 'data-hm-cta-deep' : 'data-hm-cta-standard')
          : toneSpectrum.getAttribute('data-hm-cta-unavailable');
        if (ctaPrice) ctaPrice.textContent = price;
        if (upsellCopy) upsellCopy.textContent = toneSpectrum.getAttribute(isDeep ? 'data-hm-upsell-deep' : 'data-hm-upsell');
        if (toneWheel) {
          toneWheel.setAttribute('aria-valuenow', String(value));
          toneWheel.setAttribute('aria-valuetext', `${tone}, intensity ${value} percent, ${isAvailable ? plan.name : 'recommended plan unavailable'}`);
        }

        const planKey = `${plan.variantId}:${plan.sets}`;
        if (options.syncVariant !== false && isAvailable && planKey !== lastSyncedPlanKey) {
          lastSyncedPlanKey = planKey;
          window.requestAnimationFrame(() => {
            selectProductVariant(plan.variantId, plan.option);
            showDeepPlanNotice(isDeep);
          });
        }
      };

      if (toneWheel) {
        let dragging = false;
        let dragRect = null;
        let lastAngle = 0;
        let dragValue = Number(toneSpectrum.dataset.intensity || 58);

        const pointerAngle = (event) => {
          const rect = dragRect || toneWheel.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          return Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180 / Math.PI + 90;
        };

        const normalizedAngle = (angle) => {
          let normalized = angle;
          while (normalized > 180) normalized -= 360;
          while (normalized <= -180) normalized += 360;
          return normalized;
        };

        const absoluteValueFromPointer = (event, angle) => {
          const rect = dragRect || toneWheel.getBoundingClientRect();
          const normalized = normalizedAngle(angle);
          if (normalized < -150 || normalized > 150) return event.clientX >= rect.left + rect.width / 2 ? 100 : 0;
          return (normalized + 150) / 3;
        };

        toneWheel.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          dragging = true;
          dragRect = toneWheel.getBoundingClientRect();
          lastAngle = normalizedAngle(pointerAngle(event));
          dragValue = absoluteValueFromPointer(event, lastAngle);
          toneWheel.classList.add('hm-is-dragging');
          toneWheel.setPointerCapture?.(event.pointerId);
          setIntensity(dragValue);
        });

        toneWheel.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const angle = normalizedAngle(pointerAngle(event));
          let delta = angle - lastAngle;
          if (delta > 180) delta -= 360;
          if (delta < -180) delta += 360;
          dragValue = clamp(dragValue + delta / 3, 0, 100);
          lastAngle = angle;
          setIntensity(dragValue);
        });

        const stopDragging = () => {
          dragging = false;
          dragRect = null;
          toneWheel.classList.remove('hm-is-dragging');
        };
        toneWheel.addEventListener('pointerup', stopDragging);
        toneWheel.addEventListener('pointercancel', stopDragging);
        toneWheel.addEventListener('lostpointercapture', stopDragging);
        toneWheel.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const current = Number(toneSpectrum.dataset.intensity || 58);
          if (event.key === 'Home') return setIntensity(0);
          if (event.key === 'End') return setIntensity(100);
          setIntensity(current + (['ArrowRight', 'ArrowUp'].includes(event.key) ? 1 : -1));
        });
      }

      const selectProductVariant = (variantId, optionFallback) => {
        const productRoot = document.querySelector('.hiskin-mhtp-product');
        const picker = productRoot?.querySelector('variant-selects');
        if (!productRoot || !variantId) return false;

        let variant = null;
        const variantJson = picker?.querySelector('script[type="application/json"]');
        if (variantJson) {
          try {
            variant = JSON.parse(variantJson.textContent).find((item) => String(item.id) === String(variantId));
          } catch (error) {
            variant = null;
          }
        }

        const optionValues = variant?.options?.length ? variant.options : [optionFallback];
        let changed = false;
        optionValues.forEach((optionValue, optionIndex) => {
          if (!optionValue || !picker) return;
          const group = picker.querySelectorAll('.product-form__input')[optionIndex] || picker;
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => input.value === optionValue && !input.classList.contains('disabled'));
          const select = Array.from(group.querySelectorAll('select')).find((item) => Array.from(item.options).some((option) => option.value === optionValue && !option.disabled));
          if (radio) {
            if (!radio.checked) radio.click();
            changed = true;
          } else if (select) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            changed = true;
          }
        });

        if (!changed) {
          const variantInput = productRoot.querySelector('form[action*="/cart/add"] input[name="id"]');
          if (variantInput) {
            variantInput.value = variantId;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            changed = true;
          }
        }

        const stickyPicker = productRoot.querySelector('sticky-atc');
        const stickyCombined = stickyPicker?.querySelector('.sticky-atc__variant-select');
        const stickyOption = stickyCombined
          ? Array.from(stickyCombined.options).find((option) => String(option.dataset.variantId) === String(variantId))
          : null;
        if (stickyCombined && stickyOption && stickyCombined.value !== stickyOption.value) {
          stickyCombined.value = stickyOption.value;
          stickyCombined.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return changed;
      };

      const showDeepPlanNotice = (isDeep) => {
        const productRoot = document.querySelector('.hiskin-mhtp-product');
        const picker = productRoot?.querySelector('variant-selects');
        if (!productRoot || !picker) return;
        let notice = productRoot.querySelector('[data-hm-deep-product-notice]');
        if (!notice) {
          notice = document.createElement('div');
          notice.className = 'hiskin-mhtp-flow-notice';
          notice.dataset.hmDeepProductNotice = '';
          notice.setAttribute('role', 'status');
          const heading = document.createElement('strong');
          heading.textContent = toneSpectrum.getAttribute('data-hm-product-flow-heading') || '';
          const first = document.createElement('span');
          first.textContent = `1 · ${toneSpectrum.getAttribute('data-hm-deep-step-1') || ''}`;
          const second = document.createElement('span');
          second.textContent = `2 · ${toneSpectrum.getAttribute('data-hm-deep-step-2') || ''}`;
          notice.append(heading, first, second);
          picker.insertAdjacentElement('afterend', notice);
        }
        notice.hidden = !isDeep;
      };

      const initialProductRoot = document.querySelector('.hiskin-mhtp-product');
      const initialVariantId = initialProductRoot?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value || '';
      const initialOptionValue = initialProductRoot?.querySelector('variant-selects input[type="radio"]:checked, variant-selects select')?.value || '';
      const initialPlan = Object.values(productPlans).find((candidate) => (
        (initialVariantId && String(candidate.variantId) === String(initialVariantId)) ||
        (initialOptionValue && candidate.option === initialOptionValue)
      ));
      if (initialPlan) lastSyncedPlanKey = `${initialPlan.variantId}:1`;
      setIntensity(initialPlan ? representativeIntensity[initialPlan.boxes] : Number(toneSpectrum.dataset.intensity || 58), { syncVariant: false });

      const priceObserver = new MutationObserver(() => setIntensity(Number(toneSpectrum.dataset.intensity || 58), { syncVariant: false }));
      Object.values(priceSources).forEach((source) => priceObserver.observe(source, { childList: true, characterData: true, subtree: true }));

      planCta?.addEventListener('click', () => {
        if (planCta.disabled) return;
        const isDeep = planCta.dataset.hmSets === '2';
        selectProductVariant(planCta.dataset.hmVariantId, planCta.dataset.hmVariantOption);
        [120, 600, 1200].forEach((delay) => window.setTimeout(() => showDeepPlanNotice(isDeep), delay));
        document.dispatchEvent(new CustomEvent('hiskin:finder-plan-selected', {
          detail: { variantId: planCta.dataset.hmVariantId, sets: Number(planCta.dataset.hmSets || 1) }
        }));
        window.setTimeout(() => document.querySelector('#hiskin-mhtp-choose')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' }), 180);
      });

      document.addEventListener('change', (event) => {
        if (!event.isTrusted) return;
        const isMainPicker = event.target.closest?.('.hiskin-mhtp-product variant-selects');
        const isStickyPicker = event.target.closest?.('.hiskin-mhtp-product sticky-atc');
        if (!isMainPicker && !isStickyPicker) return;
        showDeepPlanNotice(false);
        const selectedValue = event.target.value || '';
        const selectedVariantId = event.target.selectedOptions?.[0]?.dataset?.variantId || '';
        const selectedPlan = Object.values(productPlans).find((candidate) => (
          (selectedVariantId && String(candidate.variantId) === String(selectedVariantId)) ||
          (selectedValue && candidate.option === selectedValue)
        ));
        if (!selectedPlan) return;
        const selectedIntensity = representativeIntensity[selectedPlan.boxes];
        lastSyncedPlanKey = `${selectedPlan.variantId}:1`;
        setIntensity(selectedIntensity, { syncVariant: false });
      });

      toneSpectrum.querySelector('[data-hm-compare-plans]')?.addEventListener('click', (event) => {
        event.preventDefault();
        document.querySelector('#hiskin-mhtp-choose')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }

    story.querySelectorAll('.hm-faq details').forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        story.querySelectorAll('.hm-faq details[open]').forEach((openItem) => {
          if (openItem !== item) openItem.removeAttribute('open');
        });
      });
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(setupStory);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
